// =============================================
// Franchisee Operations — territory config, demo profiles, mock-stat helpers
// =============================================
// IMPORTANT: regions like "Prairies"/"Maritimes" are NOT yet a DB table — they
// are grouped here as config (a future `site_mstr_regions` table). Several stats
// have NO data source yet (area population, total businesses, revenue, missed
// opportunity, postal codes within a radius) — those are generated as MOCK,
// deterministic, clearly-labeled illustrative numbers. Real client counts ARE
// pulled from the live tables in the page (user_client_personal/business).
// =============================================

export type FranchiseLevelCode =
  | 'MASTER' | 'COUNTRY' | 'REGION' | 'STATE' | 'COUNTY' | 'CITY' | 'INDIVIDUAL';

export const LEVEL_ORDER: FranchiseLevelCode[] = [
  'MASTER', 'COUNTRY', 'REGION', 'STATE', 'COUNTY', 'CITY', 'INDIVIDUAL',
];

export const LEVEL_LABEL: Record<FranchiseLevelCode, string> = {
  MASTER: 'Master Franchisee',
  COUNTRY: 'Country Franchisee',
  REGION: 'Regional Franchisee',
  STATE: 'State / Province Franchisee',
  COUNTY: 'County / District Franchisee',
  CITY: 'City Franchisee',
  INDIVIDUAL: 'Individual Franchisee',
};

// =============================================
// Region groupings (by ISO state_code) per country.
// =============================================
export interface RegionGroup { name: string; stateCodes: string[]; }

export const REGION_GROUPS: Record<string, RegionGroup[]> = {
  Canada: [
    { name: 'Atlantic (Maritimes)', stateCodes: ['NL', 'PE', 'NS', 'NB'] },
    { name: 'Central Canada',       stateCodes: ['ON', 'QC'] },
    { name: 'Prairies',             stateCodes: ['AB', 'SK', 'MB'] },
    { name: 'West Coast',           stateCodes: ['BC'] },
    { name: 'Northern',             stateCodes: ['YT', 'NT', 'NU'] },
  ],
  'United States': [
    { name: 'Northeast', stateCodes: ['ME', 'NH', 'VT', 'MA', 'RI', 'CT', 'NY', 'NJ', 'PA'] },
    { name: 'Midwest',   stateCodes: ['OH', 'MI', 'IN', 'IL', 'WI', 'MN', 'IA', 'MO', 'ND', 'SD', 'NE', 'KS'] },
    { name: 'South',     stateCodes: ['DE', 'MD', 'DC', 'VA', 'WV', 'NC', 'SC', 'GA', 'FL', 'KY', 'TN', 'AL', 'MS', 'AR', 'LA', 'OK', 'TX'] },
    { name: 'West',      stateCodes: ['MT', 'ID', 'WY', 'CO', 'NM', 'AZ', 'UT', 'NV', 'CA', 'OR', 'WA', 'AK', 'HI'] },
  ],
  India: [
    { name: 'North India',     stateCodes: ['JK', 'LA', 'HP', 'PB', 'CH', 'UK', 'HR', 'DL', 'UP', 'RJ'] },
    { name: 'West India',      stateCodes: ['GJ', 'MH', 'GA', 'DN'] },
    { name: 'South India',     stateCodes: ['KA', 'KL', 'TN', 'AP', 'TS', 'PY', 'LD', 'AN'] },
    { name: 'East India',      stateCodes: ['BR', 'JH', 'OD', 'WB', 'SK'] },
    { name: 'Central India',   stateCodes: ['MP', 'CT'] },
    { name: 'Northeast India', stateCodes: ['AS', 'AR', 'MN', 'ML', 'MZ', 'NL', 'TR'] },
  ],
};

/** Region groups for a country, or a single synthetic "All" group when none defined. */
export function regionsForCountry(country: string, allStateCodes: string[]): RegionGroup[] {
  const defined = REGION_GROUPS[country];
  if (defined && defined.length) return defined;
  return [{ name: 'All States / Provinces', stateCodes: allStateCodes }];
}

/** Which region a given state belongs to (or 'Other'). */
export function regionOfState(country: string, stateCode: string): string {
  const groups = REGION_GROUPS[country] || [];
  const hit = groups.find(g => g.stateCodes.includes(stateCode));
  return hit ? hit.name : 'Other';
}

// =============================================
// Territory descriptor — what the current franchisee owns.
// =============================================
export interface FranTerritory {
  scope: 'worldwide' | 'country' | 'region' | 'state' | 'county' | 'city' | 'point';
  country: string;          // focus country (default Canada)
  regionName?: string;      // e.g. 'Prairies'
  ownedStateCodes?: string[]; // provinces this franchisee holds (paint these)
  cityName?: string;
  lat?: number;
  lng?: number;
  label: string;            // human-readable territory label
}

// A demo territory per level so the page (and the admin "Preview level" switcher)
// can show every view described, without needing a fully-seeded franchise tree.
export function demoTerritory(level: FranchiseLevelCode): FranTerritory {
  switch (level) {
    case 'MASTER':
      return { scope: 'worldwide', country: 'Canada', label: 'Worldwide — all countries' };
    case 'COUNTRY':
      return { scope: 'country', country: 'Canada', label: 'Canada — all provinces & territories' };
    case 'REGION':
      return { scope: 'region', country: 'Canada', regionName: 'Prairies',
               ownedStateCodes: ['AB', 'SK', 'MB'], label: 'Prairies (AB · SK · MB)' };
    case 'STATE':
      return { scope: 'state', country: 'Canada', ownedStateCodes: ['ON'],
               label: 'Ontario' };
    case 'COUNTY':
      return { scope: 'county', country: 'Canada', ownedStateCodes: ['ON'],
               cityName: 'Peel Region', lat: 43.7315, lng: -79.7624,
               label: 'Peel Region, Ontario' };
    case 'CITY':
      return { scope: 'city', country: 'Canada', ownedStateCodes: ['ON'],
               cityName: 'Mississauga', lat: 43.589, lng: -79.6441,
               label: 'Mississauga, Ontario' };
    case 'INDIVIDUAL':
    default:
      return { scope: 'point', country: 'Canada', ownedStateCodes: ['ON'],
               cityName: 'Mississauga', lat: 43.589, lng: -79.6441,
               label: 'Mississauga (10 km service area)' };
  }
}

// =============================================
// Deterministic mock-number generator (stable per seed string).
// =============================================
function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seededInt(seed: string, min: number, max: number): number {
  const h = hashSeed(seed);
  return min + (h % (max - min + 1));
}

// Rough population/business scale by level, so a Country shows millions and an
// Individual shows a neighbourhood. Purely illustrative.
const LEVEL_SCALE: Record<FranchiseLevelCode, number> = {
  MASTER: 8_000_000, COUNTRY: 38_000_000, REGION: 6_500_000,
  STATE: 14_000_000, COUNTY: 1_400_000, CITY: 720_000, INDIVIDUAL: 95_000,
};

export interface MockAreaStats {
  population: number;
  totalBusinesses: number;
  estRevenue: number;       // annual, illustrative
  subFranchisees: number;   // active franchisees under this territory
  // "missed opportunity" = businesses in the area not yet clients
  marketBusinesses: number;
  capturedBusinesses: number;
  missedBusinesses: number;
  capturePct: number;
}

/** Mock area stats keyed off a stable seed (territory label) + the real captured count. */
export function mockAreaStats(
  level: FranchiseLevelCode,
  seed: string,
  capturedBusinesses: number,
): MockAreaStats {
  const scale = LEVEL_SCALE[level] ?? 100_000;
  const population = Math.round(scale * (0.85 + (seededInt(seed + 'pop', 0, 30) / 100)));
  // ~ 1 business per 11 people, jittered
  const totalBusinesses = Math.round(population / seededInt(seed + 'den', 9, 13));
  const marketBusinesses = totalBusinesses;
  const captured = Math.min(capturedBusinesses, marketBusinesses);
  const missed = Math.max(marketBusinesses - captured, 0);
  const capturePct = marketBusinesses > 0
    ? Math.round((captured / marketBusinesses) * 1000) / 10 : 0;
  const estRevenue = captured * seededInt(seed + 'rev', 1200, 4800); // annual $/client
  const subFranchisees = level === 'INDIVIDUAL'
    ? 0 : seededInt(seed + 'sub', level === 'MASTER' ? 40 : 2, level === 'MASTER' ? 220 : 28);
  return {
    population, totalBusinesses, estRevenue, subFranchisees,
    marketBusinesses, capturedBusinesses: captured, missedBusinesses: missed, capturePct,
  };
}

/** Mock "potential opportunity" postal codes within ~10 km (individual/city). */
export function mockPotentialPostalCodes(seed: string, country: string): string[] {
  const n = seededInt(seed + 'pc', 6, 12);
  const out: string[] = [];
  const caLetters = 'LKMNPR';
  for (let i = 0; i < n; i++) {
    if (country === 'Canada') {
      const a = caLetters[seededInt(seed + i + 'a', 0, caLetters.length - 1)];
      const d1 = seededInt(seed + i + 'd', 0, 9);
      const l2 = String.fromCharCode(65 + seededInt(seed + i + 'l', 0, 25));
      const d2 = seededInt(seed + i + 'e', 0, 9);
      const l3 = String.fromCharCode(65 + seededInt(seed + i + 'm', 0, 25));
      const d3 = seededInt(seed + i + 'f', 0, 9);
      out.push(`${a}${d1}${l2} ${d2}${l3}${d3}`);
    } else {
      out.push(String(seededInt(seed + i + 'us', 10000, 99999)));
    }
  }
  return Array.from(new Set(out));
}

/** Mock "develop a new franchisee here" leads for city/county/region/etc. */
export interface DevelopLead { area: string; businesses: number; population: number; }
export function mockDevelopLeads(seed: string, areaNames: string[]): DevelopLead[] {
  return areaNames.map((area, i) => ({
    area,
    businesses: seededInt(seed + area + i + 'b', 120, 2400),
    population: seededInt(seed + area + i + 'p', 8000, 240000),
  }));
}

export function fmtMoney(n: number): string {
  return '$' + n.toLocaleString('en-US');
}
export function fmtNum(n: number): string {
  return n.toLocaleString('en-US');
}

// =============================================
// Playground demo tree — keyed by franchise_code (seeded in tables.ts + migration 061).
// Territory + principal are driven from here so the seeded rows stay clean (no brittle
// geo SERIAL FKs); the painted grid matches on state_code and the maps use lat/lng.
// =============================================
export const DEMO_TERRITORY_BY_CODE: Record<string, FranTerritory> = {
  'AKT-MASTER':            { scope: 'worldwide', country: 'Canada', label: 'Worldwide — all countries' },
  'AKT-CA':                { scope: 'country', country: 'Canada', label: 'Canada — all provinces & territories' },
  'AKT-CA-PRAIRIE':        { scope: 'region', country: 'Canada', regionName: 'Prairies',
                             ownedStateCodes: ['AB', 'SK', 'MB'], label: 'Prairies (AB · SK · MB)' },
  'AKT-CA-AB':             { scope: 'state', country: 'Canada', ownedStateCodes: ['AB'], label: 'Alberta' },
  'AKT-CA-AB-CALGARY-CO':  { scope: 'county', country: 'Canada', ownedStateCodes: ['AB'],
                             cityName: 'Calgary Region', lat: 51.0447, lng: -114.0719, label: 'Calgary Region, Alberta' },
  'AKT-CA-AB-CALGARY-CITY':{ scope: 'city', country: 'Canada', ownedStateCodes: ['AB'],
                             cityName: 'Calgary', lat: 51.0447, lng: -114.0719, label: 'City of Calgary, Alberta' },
  'AKT-CA-AB-CALGARY-IND': { scope: 'point', country: 'Canada', ownedStateCodes: ['AB'],
                             cityName: 'Calgary', lat: 51.0486, lng: -114.0708, label: 'Calgary downtown (10 km service area)' },
};

export const DEMO_PRINCIPAL_BY_CODE: Record<string, string> = {
  'AKT-MASTER': 'Sat Mehta',
  'AKT-CA': 'Priya Sharma',
  'AKT-CA-PRAIRIE': 'Marc Tremblay',
  'AKT-CA-AB': 'Jordan Blake',
  'AKT-CA-AB-CALGARY-CO': 'Dana Wong',
  'AKT-CA-AB-CALGARY-CITY': 'Avi Patel',
  'AKT-CA-AB-CALGARY-IND': 'Chris Olsen',
};

/** Mock captured-client count for a previewed franchisee that isn't your own. */
export function mockCaptured(seed: string): number {
  return seededInt(seed + 'cap', 25, 900);
}
